import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/use-debounce';

export interface ItunesApp {
  trackId: number;
  trackName: string;
  artistName: string;
  artworkUrl100: string;
  artworkUrl60: string;
  primaryGenreName: string;
  description?: string;
  bundleId: string;
}

export const useItunesSearch = (query: string) => {
  const [results, setResults] = useState<ItunesApp[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const debouncedQuery = useDebounce(query, 500);

  useEffect(() => {
    const searchApps = async () => {
      if (!debouncedQuery || debouncedQuery.trim().length < 2) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `https://itunes.apple.com/search?term=${encodeURIComponent(debouncedQuery)}&entity=software&limit=20`
        );
        
        if (!response.ok) {
          throw new Error('Failed to search apps');
        }

        const data = await response.json();
        setResults(data.results || []);
      } catch (err) {
        console.error('iTunes search error:', err);
        setError(err instanceof Error ? err.message : 'Failed to search apps');
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    searchApps();
  }, [debouncedQuery]);

  return { results, isLoading, error };
};
